import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HolaVidaLogo from "src/assets/icons/HolaVidaLogo";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { supabaseClient } from "src/supabase/supabaseClient";

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [displayAlert, setDisplayAlert] = useState<boolean>(false);
  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);
  const errorMessage = useMemo(() => {
    const hash = window.location.hash.substring(1);

    const params = new URLSearchParams(hash);

    return params.get("error_description");
  }, []);

  const onSuccess = useCallback(async () => {
    await supabaseClient.auth.signOut();
    navigate("/login");
  }, [navigate]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      try {
        const { data, error } = await supabaseClient.auth.updateUser({
          password,
        });

        if (error != null) {
          setError(error.message);
        }

        if (data.user != null) {
          setLoading(false);
          setDisplayAlert(true);
          setPassword("");

          const navigateTimeout = setTimeout(async () => {
            await onSuccess();
          }, 1500);

          return () => clearTimeout(navigateTimeout);
        }
      } catch (err) {
        console.error("Unexpected error", err);
      } finally {
        setLoading(false);
      }
    },
    [onSuccess, password],
  );

  return errorMessage ? (
    <Typography variant="h4">{errorMessage}</Typography>
  ) : (
    <Stack
      alignItems="center"
      justifyContent="center"
      height="100%"
      padding="1rem"
    >
      <Stack gap="3rem">
        <Box
          component="form"
          role="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "max-contnet",
            maxWidth: "380px",
            width: "100%",
            boxShadow: 10,
            p: 5.5,
            borderRadius: "12px",
            backgroundColor: "#ffffff",
          }}
        >
          <Box sx={{ alignSelf: "center" }}>
            <HolaVidaLogo width={180} height={90} />
          </Box>
          <Stack sx={{ mt: 1 }} gap={3}>
            <Typography variant={"h5"} fontWeight={700} width={"400px"}>
              Update Password
            </Typography>
            <Stack gap={4}>
              <TextField
                type={passwordVisibility ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                id="filled-adornment-password"
                label="Password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setPasswordVisibility((o) => !o)}
                          onMouseDown={() => setPasswordVisibility((o) => !o)}
                        >
                          {passwordVisibility ? (
                            <VisibilityOutlined />
                          ) : (
                            <VisibilityOffOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {error ? (
                <Typography sx={{ color: "red" }} textAlign="center">
                  {error}
                </Typography>
              ) : null}
              <LoadingButton
                loading={loading}
                type="submit"
                disabled={displayAlert}
              >
                Submit New Password
              </LoadingButton>

              {displayAlert ? (
                <Alert severity="success">Password successfully updated</Alert>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};

export default UpdatePassword;
